export type Role = 'user' | 'assistant' | 'system' | 'tool';
export type MessageType = 'message' | 'function_call';
export type MessageStatus = 'in_progress' | 'completed';

export type TextContent = {
  type: 'text' | 'input_text';
  text: string;
};

export type AudioContent = {
  type: 'audio';
  audio: string; // Base64 encoded audio data
  transcript?: string;
};

export type FunctionCallContent = {
  type: 'function_call';
  name: string;
  arguments: string; // JSON string
};

export type Content = TextContent | AudioContent | FunctionCallContent;

export interface Message {
  id: string; // Short unique ID (max 32 chars)
  type: MessageType;
  role: Role;
  status: MessageStatus;
  content: Content;
  name?: string;
  tool_call_id?: string;
  timestamp: string; // ISO string
}

// API message format
export interface ApiMessage {
  type: MessageType;
  role: Role;
  content: Content[] | Content;
}

// Convert API message to internal format
export const fromApiMessage = (msg: ApiMessage): Omit<Message, 'id' | 'timestamp'> => {
  // Handle case where content might be undefined or not an array
  const content = Array.isArray(msg.content) ? msg.content[0] : msg.content;
  if (!content) {
    // Provide default content if none exists
    return {
      type: msg.type,
      role: msg.role,
      status: 'completed',
      content: {
        type: 'input_text',
        text: ''
      }
    };
  }
  return {
    type: msg.type,
    role: msg.role,
    status: 'completed',
    content
  };
};

// Convert internal message to API format 
export const toApiMessage = (msg: Message): ApiMessage => {
  // Don't include our internal ID when sending to API
  const { id, timestamp, status, ...apiMsg } = msg;
  return {
    ...apiMsg,
    content: [msg.content]
  };
};

// Type guards
export const isTextContent = (content: Content | undefined): content is TextContent => {
  return content !== undefined && (content.type === 'text' || content.type === 'input_text');
};

export const isAudioContent = (content: Content | undefined): content is AudioContent => {
  return content !== undefined && content.type === 'audio';
};

export const isFunctionCallContent = (content: Content | undefined): content is FunctionCallContent => {
  return content !== undefined && content.type === 'function_call';
};

import { SessionConfig, defaultSessionConfig } from '../config/realtimeConfig';

export interface ConversationHistory {
  id: string; // Short unique ID (max 32 chars)
  created: string; // ISO string
  updated: string; // ISO string
  messages: Message[];
  metadata: {
    user_id?: string;
  };
  session_config: Partial<SessionConfig>;
}

// Helper functions
export const createHistory = (sessionConfig?: Partial<SessionConfig>): ConversationHistory => {
  const now = new Date().toISOString();
  return {
    id: `hist_${Math.random().toString(36).substring(2, 15)}`,
    created: now,
    updated: now,
    messages: [],
    metadata: {},
    session_config: sessionConfig ? {
      ...defaultSessionConfig,
      ...sessionConfig
    } : defaultSessionConfig
  };
};

export const addMessage = (
  history: ConversationHistory,
  message: Omit<Message, 'id' | 'timestamp'>
): ConversationHistory => {
  const newMessage: Message = {
    id: `msg_${Math.random().toString(36).substring(2, 15)}`,
    ...message,
    timestamp: new Date().toISOString()
  };

  return {
    ...history,
    updated: new Date().toISOString(),
    messages: [...history.messages, newMessage]
  };
};

export const updateMessage = (
  history: ConversationHistory,
  messageId: string,
  updates: Partial<Message>
): ConversationHistory => {
  const messages = history.messages.map(msg => 
    msg.id === messageId 
      ? { ...msg, ...updates }
      : msg
  );

  return {
    ...history,
    updated: new Date().toISOString(),
    messages
  };
};

// Convert history to API format for sending to model
export const historyToApiMessages = (history: ConversationHistory): any[] => {
  return history.messages
    .filter(msg => msg.status === 'completed')
    .map(msg => {
      if (msg.role === 'tool') {
        return {
          role: msg.role,
          content: msg.content.type === 'text' ? msg.content.text : '',
          tool_call_id: msg.tool_call_id,
          name: msg.name
        };
      }

      return {
        role: msg.role,
        content: msg.content.type === 'text' ? msg.content.text : 
                msg.content.type === 'audio' ? msg.content.transcript || '' :
                msg.content.type === 'function_call' ? null : '',
        name: msg.name
      };
    });
};

// Serialize history for storage
export const serializeHistory = (history: ConversationHistory): string => {
  return JSON.stringify(history);
};

// Deserialize history from storage
export const deserializeHistory = (serialized: string): ConversationHistory => {
  const history = JSON.parse(serialized);
  
  // Ensure dates are properly formatted
  history.created = new Date(history.created).toISOString();
  history.updated = new Date(history.updated).toISOString();
  history.messages.forEach((msg: Message) => {
    msg.timestamp = new Date(msg.timestamp).toISOString();
  });

  return history;
};

// Example usage:
/*
let history = createHistory('gpt-4');

// Add a user text message
history = addMessage(history, {
  type: 'message',
  role: 'user',
  status: 'completed',
  content: {
    type: 'text',
    text: 'Hello!'
  }
});

// Add a user audio message
history = addMessage(history, {
  type: 'message',
  role: 'user',
  status: 'in_progress',
  content: {
    type: 'audio',
    audio: 'base64_audio_data'
  }
});

// Update audio message with transcription
history = updateMessage(history, 
  'message_id',
  {
    status: 'completed',
    content: {
      type: 'audio',
      audio: 'base64_audio_data',
      transcript: 'Hello there!'
    }
  }
);

// Add an assistant function call
history = addMessage(history, {
  type: 'function_call',
  role: 'assistant',
  status: 'completed',
  content: {
    type: 'function_call',
    name: 'get_weather',
    arguments: '{"location": "San Francisco"}'
  }
});

// Add tool response
history = addMessage(history, {
  type: 'message',
  role: 'tool',
  status: 'completed',
  content: {
    type: 'text',
    text: '{"temp": 72, "condition": "sunny"}'
  },
  tool_call_id: 'function_call_id',
  name: 'get_weather'
});

// Add final assistant response
history = addMessage(history, {
  type: 'message',
  role: 'assistant',
  status: 'completed',
  content: {
    type: 'text',
    text: 'The weather in San Francisco is sunny and 72°F'
  }
});

// Store history
localStorage.setItem('conversation', serializeHistory(history));

// Load history
const loadedHistory = deserializeHistory(localStorage.getItem('conversation'));

// Convert to API format
const apiMessages = historyToApiMessages(loadedHistory);
*/
